import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {CSSTransition} from 'react-transition-group'
import {Button, ConfirmActionSwal, DropdownButton} from '../../../../../../_gori/partials/widgets'
import {toast} from 'react-toastify'
import {CopyToClipboard} from 'react-copy-to-clipboard'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import SettingsService from '../../../core/_requests'
import {CustomerInvite} from '../../../core/_types'

const CustomerInvitePage: React.FC = () => {
  const intl = useIntl()
  const {newCancelToken, isCancel} = useCancelToken()
  const [loadingFirst, setLoadingFirst] = useState(true)
  const [customerInvite, setCustomerInvite] = useState<CustomerInvite | null>(null)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [isDisabling, setIsDisabling] = useState(false)
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false)

  useEffect(() => {
    const fetchCustomerInvite = async () => {
      try {
        const response = await SettingsService.getCustomerInvite({
          cancelToken: newCancelToken(),
        })
        setCustomerInvite(response)
      } catch (error) {
        if (isCancel(error)) return
      } finally {
        setLoadingFirst(false)
      }
    }

    fetchCustomerInvite()
  }, [newCancelToken, isCancel])

  const handleRegenerateLink = useCallback(async () => {
    setIsRegenerating(true)
    try {
      const response = await SettingsService.regenerateInviteLink(
        {},
        {cancelToken: newCancelToken()}
      )
      setCustomerInvite(response)
      toast.success(intl.formatMessage({id: 'UPDATED_SUCCESSFULLY'}))
      return true
    } catch (error) {
      if (isCancel(error)) return false
      return false
    } finally {
      setIsRegenerating(false)
    }
  }, [intl, newCancelToken, isCancel])

  const handleConfirmRegenerate = useCallback(async () => {
    await handleRegenerateLink()
    setShowRegenerateConfirm(false)
  }, [handleRegenerateLink])

  const handleDisableLink = useCallback(async () => {
    if (!customerInvite) return

    setIsDisabling(true)
    try {
      const response = await SettingsService.toggleInviteLink(
        {enabled: !customerInvite.enabled},
        {cancelToken: newCancelToken()}
      )
      setCustomerInvite(response)
      toast.success(intl.formatMessage({id: 'UPDATED_SUCCESSFULLY'}))
    } catch (error) {
      if (isCancel(error)) return
    } finally {
      setIsDisabling(false)
    }
  }, [customerInvite, intl, newCancelToken, isCancel])

  const inviteUrl = customerInvite?.url ?? ''

  const buildSkeleton = useMemo(
    () => (
      <div className='card table-content h-100 border-0'>
        <div className='card-body p-0 overflow-hidden rounded'>
          <div className='px-8 py-3 customer-invite-header'>
            <span className='placeholder placeholder-lg rounded-2 bg-secondary col-3' />
          </div>
          <div className='d-flex flex-wrap align-items-center justify-content-between gap-4 px-8 py-5'>
            <span className='placeholder placeholder-lg rounded-2 bg-secondary col-2 min-w-150px' />
            <span className='placeholder placeholder-lg rounded-2 bg-secondary flex-grow-1' />
            <div className='d-flex align-items-center gap-3'>
              <span className='btn placeholder placeholder-lg rounded-2 bg-secondary col-3' />
              <span className='btn placeholder placeholder-lg rounded-2 bg-secondary col-3' />
              <span className='btn placeholder placeholder-lg rounded-2 bg-secondary col-1' />
            </div>
          </div>
        </div>
      </div>
    ),
    []
  )

  const listDropDownBtn = useMemo(
    () => [
      {
        label: intl.formatMessage({
          id: customerInvite?.enabled ? 'DISABLE_LINK' : 'ENABLE_LINK',
        }),
        action: handleDisableLink,
        className: 'cursor-pointer',
        isVisible: !!customerInvite,
      },
    ],
    [intl, handleDisableLink, customerInvite]
  )

  return (
    <>
      <ConfirmActionSwal
        show={showRegenerateConfirm}
        title={intl.formatMessage({id: 'REGENERATE_LINK'})}
        message={intl.formatMessage({id: 'REGENERATE_LINK_CONFIRM_MESSAGE'})}
        messageCancel={intl.formatMessage({id: 'CANCEL'})}
        confirmButtonText='REGENERATE_LINK'
        handleCallBack={handleConfirmRegenerate}
        handleClose={() => setShowRegenerateConfirm(false)}
      />
      <div className='d-flex justify-content-start w-100 settings-header table-settings-header mb-8'>
        <h1 className='d-flex flex-column text-dark fw-boldest my-0 fs-1'>
          {intl.formatMessage({id: 'CUSTOMER_INVITE'})}
        </h1>
      </div>
      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        {loadingFirst ? (
          buildSkeleton
        ) : (
          <div className='card table-content h-100 border-0'>
            <div className='card-body p-0 overflow-hidden rounded'>
              <div className='px-8 py-3 text-uppercase fw-bold customer-invite-header'>
                {intl.formatMessage({id: 'V2_PRODUCTION'})}
              </div>
              <div className='d-flex flex-wrap align-items-center justify-content-between gap-4 px-8 py-5'>
                <div className='min-w-150px customer-invite-text col-2'>
                  {intl.formatMessage({id: 'PRODUCTION_URL'})}
                </div>
                <div className='flex-grow-1 customer-invite-text col-5'>{inviteUrl}</div>
                <div className='d-flex align-items-center gap-3 flex-1 justify-content-end'>
                  <CopyToClipboard
                    text={inviteUrl}
                    onCopy={() => toast.success(intl.formatMessage({id: 'COPIED'}))}
                  >
                    <div>
                      <button
                        type='button'
                        className='btn btn-advanced-primary btn-sm'
                        disabled={!customerInvite?.enabled || !inviteUrl}
                      >
                        {intl.formatMessage({id: 'COPY_LINK'})}
                      </button>
                    </div>
                  </CopyToClipboard>

                  <Button
                    className='btn btn-advanced-secondary btn-sm'
                    label={intl.formatMessage({id: 'REGENERATE_LINK'})}
                    loadingText={intl.formatMessage({id: 'REGENERATE_LINK'})}
                    disabled={isRegenerating}
                    loading={isRegenerating}
                    event={() => setShowRegenerateConfirm(true)}
                  />
                  <DropdownButton
                    loading={isDisabling}
                    list={listDropDownBtn.filter((x) => x.isVisible)}
                    className='d-flex justify-content-center'
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </CSSTransition>
    </>
  )
}

export {CustomerInvitePage}
